import React, { useState } from "react";
import { 
  BookOpen, 
  Plus, 
  Users, 
  Calendar, 
  CheckSquare, 
  Trash2, 
  Edit3, 
  Sparkles, 
  Compass,
  CheckCircle2
} from "lucide-react";

export const InstructionGroups = ({
  groups,
  members,
  onOpenNewGroupModal,
  onOpenEditGroupModal,
  onDeleteGroup
}) => {
  const [selectedCategory, setSelectedCategory] = useState("all");

  const filteredGroups = groups.filter((g) => {
    if (selectedCategory === "all") return true;
    return g.category === selectedCategory;
  });

  return (
    <div className="space-y-6" data-testid="instruction-groups-section">
      
      {/* Header & New Group */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-serif font-bold text-white">Câmaras de Instrução & Comissões</h2>
          <p className="text-xs text-slate-400">
            Programas de estudo maçônico por grau, comissões de finanças, beneficência e tarefas litúrgicas
          </p>
        </div>

        <button
          data-testid="btn-open-new-group-modal"
          onClick={onOpenNewGroupModal}
          className="flex items-center space-x-1.5 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(212,175,55,0.25)]"
        >
          <Plus className="w-4 h-4" />
          <span>Novo Grupo / Programa</span>
        </button>
      </div>

      {/* Category Filter Pills */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: "all", label: "Todos os Grupos" },
          { id: "Câmara de Instrução", label: "Câmaras de Instrução (Graus 1, 2 e 3)" },
          { id: "Comissão Permanente", label: "Comissões Permanentes" },
          { id: "Ação Filantrópica", label: "Ações Filantrópicas" }
        ].map((tab) => (
          <button
            key={tab.id}
            data-testid={`filter-group-category-${tab.id}`}
            onClick={() => setSelectedCategory(tab.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              selectedCategory === tab.id
                ? "bg-[#D4AF37] text-slate-950 shadow-[0_0_10px_rgba(212,175,55,0.3)]"
                : "bg-[#131C2E] text-slate-400 hover:text-white border border-slate-800"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Groups Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredGroups.map((group) => {
          return (
            <div
              key={group.id}
              data-testid={`group-card-${group.id}`}
              className="luxury-card p-6 flex flex-col justify-between hover:border-[#D4AF37]/60 transition-all space-y-5"
            >
              <div>
                {/* Header of Group */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#131C2E] border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37]">
                      <BookOpen className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-serif font-bold text-base text-white leading-tight">
                        {group.name}
                      </h3>
                      <span className="text-xs text-slate-400">{group.category}</span>
                    </div>
                  </div>

                  <span className="text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30">
                    Alvo: {group.degree_target}
                  </span>
                </div>

                <p className="text-xs text-slate-300 mt-2 leading-relaxed">
                  {group.description}
                </p>

                {/* Coordinator & Schedule */}
                <div className="mt-4 p-3 rounded-xl bg-[#080C14] border border-slate-800 space-y-1.5 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Coordenador / Responsável:</span>
                    <span className="font-semibold text-white">{group.coordinator_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Cronograma de Encontros:</span>
                    <span className="font-mono text-[#D4AF37]">{group.meeting_schedule}</span>
                  </div>
                </div>

                {/* Topics & Study Tasks */}
                {group.topics_or_tasks && group.topics_or_tasks.length > 0 && (
                  <div className="mt-4 space-y-1.5">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block">
                      Temas e Peças de Arquitetura ({group.topics_or_tasks.length}):
                    </span>
                    <div className="space-y-1">
                      {group.topics_or_tasks.map((topic, idx) => (
                        <div key={idx} className="flex items-start space-x-2 text-xs text-slate-300">
                          <CheckCircle2 className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 shrink-0" />
                          <span>{topic}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="pt-3 border-t border-slate-800 flex items-center justify-between">
                <span className="text-[11px] text-slate-500">
                  {group.member_ids?.length || 0} Irmãos participantes
                </span>

                <div className="flex items-center space-x-2">
                  <button
                    data-testid={`btn-edit-group-${group.id}`}
                    onClick={() => onOpenEditGroupModal(group)}
                    title="Editar Grupo"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    data-testid={`btn-delete-group-${group.id}`}
                    onClick={() => onDeleteGroup(group)}
                    title="Excluir Grupo"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

            </div>
          );
        })}
      </div>

    </div>
  );
};
